import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Tab,
  Nav,
  Spinner,
  Modal,
  Button,
  Form,
  Badge,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../../styles/UserPage.css';
import type { UserDTO } from '../../dto/UserDTO';
import type { AddressDTO } from '../../dto/AddressDTO';
import { showError, showSuccess } from '../../components/Alert';
import { apiClient } from '../../config/ApiClient';
import { updateUser, updatePassword } from '../../services/user';

import UserOrderPage from './UserOrderPage';

const UserPage: React.FC = () => {
  const navigate = useNavigate();
  const storedUserName = localStorage.getItem('userName');
  const token = localStorage.getItem('token');

  const [user, setUser] = useState<UserDTO>({
    userName: '',
    firstName: '',
    lastName: '',
    email: '',
    storeName: '',
    addresses: [],
  });

  const [loadingUser, setLoadingUser] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [addresses, setAddresses] = useState<AddressDTO[]>([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(
    null
  );
  const [addressDraft, setAddressDraft] = useState<AddressDTO>({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    defaultAddress: false,
  });
  const [passwordForm, setPasswordForm] = useState<{
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const safeTrim = (value: unknown) =>
    typeof value === 'string'
      ? value.trim()
      : value
      ? String(value).trim()
      : '';

  const normalizeAddress = (addr: any): AddressDTO => ({
    street1: safeTrim(addr?.street1 ?? addr?.streetAddress),
    street2: safeTrim(addr?.street2 ?? addr?.apartment),
    city: safeTrim(addr?.city),
    state: safeTrim(addr?.state ?? addr?.province),
    zip: safeTrim(addr?.zip ?? addr?.postalCode),
    country: safeTrim(addr?.country),
    defaultAddress: Boolean(addr?.defaultAddress),
  });

  const createEmptyAddress = (makeDefault: boolean): AddressDTO => ({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    defaultAddress: makeDefault,
  });

  // If not logged in → show modal; otherwise fetch user profile
  useEffect(() => {
    if (!storedUserName || !token) {
      setLoadingUser(false);
      setShowLoginModal(true);
      return;
    }

    apiClient
      .get<UserDTO>(`/user/${storedUserName}`)
      .then((res) => {
        const normalizedAddresses = (res.data.addresses ?? []).map((addr) =>
          normalizeAddress(addr)
        );
        setUser({ ...res.data, addresses: normalizedAddresses });
        setAddresses(normalizedAddresses);
      })
      .catch((err) => {
        console.error('[UserPage] Error fetching user:', err);
        showError(err.response?.data?.message || 'Failed to load user');
      })
      .finally(() => setLoadingUser(false));
  }, [storedUserName, token]);

  useEffect(() => {
    setFormState({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
    });
    setAddresses((user.addresses ?? []).map((addr) => normalizeAddress(addr)));
  }, [user]);

  if (loadingUser) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const onFieldChange =
    (key: keyof typeof formState) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setFormState((prev) => ({ ...prev, [key]: value }));
    };

  const onPasswordFieldChange =
    (key: keyof typeof passwordForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setPasswordForm((prev) => ({ ...prev, [key]: value }));
    };

  const resetPasswordForm = () =>
    setPasswordForm({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

  const handlePasswordCancel = () => {
    if (isUpdatingPassword) return;
    resetPasswordForm();
    setShowPasswordForm(false);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUpdatingPassword) return;

    const oldPassword = passwordForm.oldPassword.trim();
    const newPassword = passwordForm.newPassword.trim();
    const confirmPassword = passwordForm.confirmPassword.trim();

    if (!oldPassword || !newPassword || !confirmPassword) {
      showError('Please complete all password fields.');
      return;
    }

    if (newPassword.length < 8) {
      showError('New password must be at least 8 characters long.');
      return;
    }

    if (oldPassword === newPassword) {
      showError('New password must be different from the current password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('New password and confirmation do not match.');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      await updatePassword({ oldPassword, newPassword });
      showSuccess('Password updated successfully.');
      resetPasswordForm();
      setShowPasswordForm(false);
    } catch (err: any) {
      console.error('[UserPage] Error updating password:', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to update password.';
      showError(
        typeof message === 'string' ? message : 'Failed to update password.'
      );
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const onCancelEdit = () => {
    setFormState({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
    });
    setAddresses((user.addresses ?? []).map((addr) => normalizeAddress(addr)));
    setShowAddressModal(false);
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyAddress((user.addresses ?? []).length === 0));
    setIsEditing(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing || isSaving) return;

    if (!formState.firstName.trim()) {
      showError('First name cannot be empty.');
      return;
    }

    if (!formState.lastName.trim()) {
      showError('Last name cannot be empty.');
      return;
    }

    if (!formState.email.trim()) {
      showError('Email cannot be empty.');
      return;
    }

    const addressesForSave = addresses.map((addr) => {
      const street2 = safeTrim(addr.street2);
      return {
        street1: safeTrim(addr.street1),
        street2: street2 || undefined,
        city: safeTrim(addr.city),
        state: safeTrim(addr.state),
        zip: safeTrim(addr.zip),
        country: safeTrim(addr.country),
        defaultAddress: Boolean(addr.defaultAddress),
      };
    });

    if (
      addressesForSave.length > 0 &&
      !addressesForSave.some((addr) => addr.defaultAddress)
    ) {
      addressesForSave[0] = { ...addressesForSave[0], defaultAddress: true };
    }

    if (addressesForSave.length === 0) {
      showError('Please add at least one address.');
      return;
    }

    const payload: UserDTO = {
      userName: user.userName,
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
      email: formState.email.trim(),
      addresses: addressesForSave,
      storeName: user.storeName,
    };

    if (!payload.storeName) {
      delete payload.storeName;
    }

    try {
      setIsSaving(true);
      await updateUser(payload);

      const sanitizedUser: UserDTO = {
        userName: payload.userName,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        addresses: addressesForSave,
        storeName: payload.storeName,
      };

      setUser(sanitizedUser);
      setAddresses(addressesForSave.map((addr) => normalizeAddress(addr)));
      setFormState({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
      });
      showSuccess('Profile updated successfully.');
      setIsEditing(false);
    } catch (err: any) {
      console.error('[UserPage] Error updating user:', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to update profile.';
      showError(
        typeof message === 'string' ? message : 'Failed to update profile.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const persistAddresses = async (
    nextAddresses: AddressDTO[],
    successMessage: string
  ): Promise<boolean> => {
    const sanitized = nextAddresses.map((addr) => {
      const street2 = safeTrim(addr.street2);
      return {
        street1: safeTrim(addr.street1),
        street2: street2 || undefined,
        city: safeTrim(addr.city),
        state: safeTrim(addr.state),
        zip: safeTrim(addr.zip),
        country: safeTrim(addr.country),
        defaultAddress: Boolean(addr.defaultAddress),
      };
    });

    if (
      sanitized.length > 0 &&
      !sanitized.some((addr) => addr.defaultAddress)
    ) {
      sanitized[0] = { ...sanitized[0], defaultAddress: true };
    }

    if (sanitized.length === 0) {
      showError('Please add at least one address.');
      return false;
    }

    const payload: UserDTO = {
      userName: user.userName,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      addresses: sanitized,
      storeName: user.storeName,
    };

    if (!payload.storeName) {
      delete payload.storeName;
    }

    try {
      setAddressSaving(true);
      await updateUser(payload);

      const normalized = sanitized.map((addr) => normalizeAddress(addr));
      setUser((prev) => ({
        ...prev,
        addresses: normalized,
      }));
      setAddresses(normalized);
      showSuccess(successMessage);
      return true;
    } catch (err: any) {
      console.error('[UserPage] Error saving addresses:', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to save address.';
      showError(
        typeof message === 'string' ? message : 'Failed to save address.'
      );
      return false;
    } finally {
      setAddressSaving(false);
    }
  };

  const handleAddressFieldChange =
    (key: keyof AddressDTO) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setAddressDraft((prev) => ({ ...prev, [key]: value }));
    };

  const handleAddressDefaultToggle = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = e.target;
    setAddressDraft((prev) => ({ ...prev, defaultAddress: checked }));
  };

  const handleAddAddress = () => {
    if (addressSaving || isSaving) return;
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyAddress(addresses.length === 0));
    setShowAddressModal(true);
  };

  const handleEditAddress = (index: number) => {
    if (addressSaving || isSaving) return;
    const addr = addresses[index];
    if (!addr) return;
    setEditingAddressIndex(index);
    setAddressDraft({
      street1: addr.street1 ?? '',
      street2: addr.street2 ?? '',
      city: addr.city ?? '',
      state: addr.state ?? '',
      zip: addr.zip ?? '',
      country: addr.country ?? '',
      defaultAddress: addr.defaultAddress,
    });
    setShowAddressModal(true);
  };

  const handleDeleteAddress = (index: number) => {
    if (addressSaving || isSaving) return;
    if (addresses.length <= 1) {
      showError('At least one address is required.');
      return;
    }
    const updated = addresses.filter((_, i) => i !== index);
    let normalized = updated;
    if (
      normalized.length > 0 &&
      !normalized.some((addr) => addr.defaultAddress)
    ) {
      normalized = normalized.map((addr, idx) => ({
        ...addr,
        defaultAddress: idx === 0,
      }));
    }
    void persistAddresses(
      normalized.map((addr) => normalizeAddress(addr)),
      'Address removed.'
    );
  };

  const handleAddressModalClose = () => {
    setShowAddressModal(false);
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyAddress(addresses.length === 0));
  };

  const handleAddressSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (addressSaving || isSaving) return;
    const trimmedDraft: AddressDTO = {
      street1: safeTrim(addressDraft.street1),
      street2: safeTrim(addressDraft.street2),
      city: safeTrim(addressDraft.city),
      state: safeTrim(addressDraft.state),
      zip: safeTrim(addressDraft.zip),
      country: safeTrim(addressDraft.country),
      defaultAddress: Boolean(addressDraft.defaultAddress),
    };

    if (!trimmedDraft.street1) {
      showError('Street address is required.');
      return;
    }
    if (!trimmedDraft.city) {
      showError('City is required.');
      return;
    }
    if (!trimmedDraft.state) {
      showError('State / Province is required.');
      return;
    }
    if (!trimmedDraft.zip) {
      showError('Postal / ZIP code is required.');
      return;
    }
    if (!trimmedDraft.country) {
      showError('Country is required.');
      return;
    }

    let updatedAddresses = [...addresses];
    let targetIndex: number;

    if (editingAddressIndex !== null) {
      targetIndex = editingAddressIndex;
      updatedAddresses[editingAddressIndex] = trimmedDraft;
    } else {
      updatedAddresses.push(trimmedDraft);
      targetIndex = updatedAddresses.length - 1;
    }

    if (trimmedDraft.defaultAddress) {
      updatedAddresses = updatedAddresses.map((addr, idx) => ({
        ...addr,
        defaultAddress: idx === targetIndex,
      }));
    } else if (!updatedAddresses.some((addr) => addr.defaultAddress)) {
      updatedAddresses = updatedAddresses.map((addr, idx) => ({
        ...addr,
        defaultAddress: idx === targetIndex,
      }));
    }

    void (async () => {
      const success = await persistAddresses(
        updatedAddresses.map((addr) => normalizeAddress(addr)),
        editingAddressIndex !== null ? 'Address updated.' : 'Address added.'
      );
      if (success) {
        setShowAddressModal(false);
        setEditingAddressIndex(null);
        setAddressDraft(createEmptyAddress(updatedAddresses.length === 0));
      }
    })();
  };

  const disableDefaultToggle = addressSaving;

  return (
    <>
      <Modal
        show={showLoginModal}
        backdrop="static"
        keyboard={false}
        onHide={() => {
          setShowLoginModal(false);
          navigate('/login');
        }}
      >
        <Modal.Header>
          <Modal.Title>Login Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>You must log in to access your profile.</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Modal.Footer>
      </Modal>

      <Container className="user-page-container my-4">
        <Tab.Container defaultActiveKey="orders">
          <Row>
            <Col sm={2}>
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link eventKey="user">Profile</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="orders">My Orders</Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>

            <Col sm={10}>
              <Tab.Content>
                <Tab.Pane eventKey="user">
                  <Form onSubmit={onSubmit} className="profile-form">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Profile Details</h5>
                      <div className="d-flex gap-2">
                        {isEditing ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={onCancelEdit}
                              disabled={isSaving}
                            >
                              Cancel
                            </Button>
                            <Button type="submit" size="sm" disabled={isSaving}>
                              {isSaving ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setIsEditing(true)}
                          >
                            Edit Profile
                          </Button>
                        )}
                      </div>
                    </div>
                    <Row>
                      <Col md={6}>
                        <Form.Group
                          controlId="profileUsername"
                          className="mb-3"
                        >
                          <Form.Label>Username</Form.Label>
                          <Form.Control
                            type="text"
                            value={user.userName}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="profileEmail" className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={formState.email}
                            onChange={onFieldChange('email')}
                            disabled={!isEditing || isSaving}
                            placeholder="Enter email"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group
                          controlId="profileFirstName"
                          className="mb-3"
                        >
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={formState.firstName}
                            onChange={onFieldChange('firstName')}
                            disabled={!isEditing || isSaving}
                            placeholder="Enter first name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group
                          controlId="profileLastName"
                          className="mb-3"
                        >
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={formState.lastName}
                            onChange={onFieldChange('lastName')}
                            disabled={!isEditing || isSaving}
                            placeholder="Enter last name"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    {user.storeName && (
                      <Form.Group controlId="profileStoreName" className="mb-3">
                        <Form.Label>Store Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={user.storeName}
                          disabled
                        />
                      </Form.Group>
                    )}
                  </Form>

                  <div className="password-section mt-4">
                    <div className="section-divider" />
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Update Password</h5>
                      {showPasswordForm ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={handlePasswordCancel}
                          disabled={isUpdatingPassword}
                        >
                          Cancel
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setShowPasswordForm(true)}
                        >
                          Change Password
                        </Button>
                      )}
                    </div>
                    {showPasswordForm ? (
                      <Form
                        onSubmit={handlePasswordSubmit}
                        className="password-form"
                      >
                        <Row className="gy-3">
                          <Col md={4}>
                            <Form.Group controlId="passwordCurrent">
                              <Form.Label>Current Password</Form.Label>
                              <Form.Control
                                type="password"
                                value={passwordForm.oldPassword}
                                onChange={onPasswordFieldChange('oldPassword')}
                                placeholder="Enter current password"
                                autoComplete="current-password"
                                disabled={isUpdatingPassword}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group controlId="passwordNew">
                              <Form.Label>New Password</Form.Label>
                              <Form.Control
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={onPasswordFieldChange('newPassword')}
                                placeholder="Create new password"
                                autoComplete="new-password"
                                disabled={isUpdatingPassword}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group controlId="passwordConfirm">
                              <Form.Label>Confirm New Password</Form.Label>
                              <Form.Control
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={onPasswordFieldChange(
                                  'confirmPassword'
                                )}
                                placeholder="Confirm new password"
                                autoComplete="new-password"
                                disabled={isUpdatingPassword}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={resetPasswordForm}
                            disabled={isUpdatingPassword}
                          >
                            Clear
                          </Button>
                          <Button type="submit" disabled={isUpdatingPassword}>
                            {isUpdatingPassword
                              ? 'Updating...'
                              : 'Update Password'}
                          </Button>
                        </div>
                      </Form>
                    ) : (
                      <p className="text-muted small mb-0">
                        Select &quot;Change Password&quot; to update your
                        account password.
                      </p>
                    )}
                  </div>

                  <div className="address-section">
                    <div className="section-divider" />
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="mb-0">Addresses</h5>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddAddress}
                        disabled={addressSaving}
                      >
                        Add Address
                      </Button>
                    </div>
                    <ul className="address-list">
                      {addresses.length === 0 ? (
                        <li>No addresses on file</li>
                      ) : (
                        addresses.map((addr: AddressDTO, index) => (
                          <li key={`${addr.street1}-${addr.city}-${index}`}>
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
                              <div>
                                <div>
                                  {addr.street1}
                                  {addr.street2 ? `, ${addr.street2}` : ''}
                                </div>
                                <div>
                                  {addr.city}, {addr.state}, {addr.country}{' '}
                                  {addr.zip}
                                  {addr.defaultAddress && (
                                    <Badge bg="secondary" className="ms-2">
                                      Default
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditAddress(index)}
                                  disabled={addressSaving}
                                >
                                  Edit
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteAddress(index)}
                                  disabled={
                                    addressSaving || addresses.length <= 1
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </Tab.Pane>

                <Tab.Pane eventKey="orders">
                  <UserOrderPage />
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>

      <Modal
        show={showAddressModal}
        onHide={handleAddressModalClose}
        backdrop="static"
        keyboard={false}
        dialogClassName="user-address-modal"
      >
        <Form onSubmit={handleAddressSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingAddressIndex !== null ? 'Edit Address' : 'Add Address'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="addressStreet1" className="mb-3">
              <Form.Label>Street Address</Form.Label>
              <Form.Control
                type="text"
                value={addressDraft.street1}
                onChange={handleAddressFieldChange('street1')}
                placeholder="Street address"
              />
            </Form.Group>
            <Form.Group controlId="addressStreet2" className="mb-3">
              <Form.Label>Apartment / Suite (optional)</Form.Label>
              <Form.Control
                type="text"
                value={addressDraft.street2}
                onChange={handleAddressFieldChange('street2')}
                placeholder="Apartment, suite, etc."
              />
            </Form.Group>
            <Form.Group controlId="addressCity" className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                value={addressDraft.city}
                onChange={handleAddressFieldChange('city')}
                placeholder="City"
              />
            </Form.Group>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="addressState">
                  <Form.Label>State / Province</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressDraft.state}
                    onChange={handleAddressFieldChange('state')}
                    placeholder="State or province"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="addressZip">
                  <Form.Label>Postal / ZIP Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressDraft.zip}
                    onChange={handleAddressFieldChange('zip')}
                    placeholder="Postal or ZIP code"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId="addressCountry" className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                value={addressDraft.country}
                onChange={handleAddressFieldChange('country')}
                placeholder="Country"
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              id="addressDefault"
              label="Set as default address"
              checked={addressDraft.defaultAddress}
              onChange={handleAddressDefaultToggle}
              disabled={disableDefaultToggle}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleAddressModalClose}
              disabled={addressSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={addressSaving}>
              {addressSaving ? 'Saving…' : 'Save Address'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default UserPage;
